import { TypographyOptions } from "@mui/material/styles/createTypography";
import React from "react";

interface FontSizes {
  sm: number;
  md: number;
  lg: number;
}

function pxToRem(value: number) {
  return `${value / 16}rem`;
}

function responsiveFontSizes({ sm, md, lg }: FontSizes) {
  return {
    "@media (min-width:600px)": {
      fontSize: pxToRem(sm),
    },
    "@media (min-width:900px)": {
      fontSize: pxToRem(md),
    },
    "@media (min-width:1200px)": {
      fontSize: pxToRem(lg),
    },
  };
}

const FONT_PRIMARY = "Public Sans, sans-serif";
const FONT_MONO =
  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

const tabular = {
  fontVariantNumeric: "tabular-nums" as const,
  fontFeatureSettings: '"tnum"' as const,
};

const typography = {
  fontFamily: FONT_PRIMARY,
  fontWeightRegular: 400,
  fontWeightMedium: 600,
  fontWeightBold: 700,
  h1: {
    fontWeight: 700,
    lineHeight: 80 / 64,
    fontSize: pxToRem(40),
    ...responsiveFontSizes({ sm: 52, md: 58, lg: 64 }),
  },
  h2: {
    fontWeight: 700,
    lineHeight: 64 / 48,
    fontSize: pxToRem(32),
    ...responsiveFontSizes({ sm: 40, md: 44, lg: 48 }),
  },
  h3: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(24),
    ...responsiveFontSizes({ sm: 26, md: 30, lg: 32 }),
  },
  h4: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(20),
    ...responsiveFontSizes({ sm: 20, md: 24, lg: 24 }),
  },
  h5: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(18),
    ...responsiveFontSizes({ sm: 19, md: 20, lg: 20 }),
  },
  h6: {
    fontWeight: 700,
    lineHeight: 28 / 18,
    fontSize: pxToRem(17),
    ...responsiveFontSizes({ sm: 18, md: 18, lg: 18 }),
  },
  subtitle1: {
    fontWeight: 600,
    lineHeight: 1.5,
    fontSize: pxToRem(16),
  },
  subtitle2: {
    fontWeight: 600,
    lineHeight: 22 / 14,
    fontSize: pxToRem(14),
  },
  body1: {
    lineHeight: 1.5,
    fontSize: pxToRem(16),
  },
  body2: {
    lineHeight: 22 / 14,
    fontSize: pxToRem(14),
  },
  caption: {
    lineHeight: 1.5,
    fontSize: pxToRem(12),
  },
  overline: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(12),
    letterSpacing: 1.1,
    textTransform: "uppercase" as const,
  },
  button: {
    fontWeight: 700,
    lineHeight: 24 / 14,
    fontSize: pxToRem(14),
    textTransform: "capitalize" as const,
  },

  // ---- Custom variants ----
  pageTitle: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 600,
    fontSize: pxToRem(18),
    lineHeight: 1.2,
    letterSpacing: "-0.01em",
    "@media (min-width:900px)": {
      fontSize: pxToRem(22),
    },
  },
  pageMeta: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 400,
    fontSize: pxToRem(12),
    lineHeight: 1.5,
  },
  sectionTitle: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 600,
    fontSize: pxToRem(14),
    lineHeight: 1.4,
    letterSpacing: "-0.01em",
  },
  sectionLabel: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 600,
    fontSize: pxToRem(10.5),
    lineHeight: 1.2,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
  },
  heroAmount: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 600,
    fontSize: pxToRem(22),
    lineHeight: 1.15,
    letterSpacing: "-0.01em",
    ...tabular,
  },
  heroAmountSm: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 600,
    fontSize: pxToRem(17),
    lineHeight: 1.15,
    letterSpacing: "-0.01em",
    ...tabular,
  },
  ticker: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 700,
    fontSize: pxToRem(13),
    lineHeight: 1.2,
    letterSpacing: "0.02em",
    textTransform: "uppercase" as const,
  },
  tickerSm: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 700,
    fontSize: pxToRem(11.5),
    lineHeight: 1.2,
    letterSpacing: "0.02em",
  },
  networkBadge: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 600,
    fontSize: pxToRem(10.5),
    lineHeight: 1.2,
    letterSpacing: "0.04em",
  },
  chipLabel: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 600,
    fontSize: pxToRem(11),
    lineHeight: 1.2,
    letterSpacing: "0.04em",
  },
  statusLabel: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 600,
    fontSize: pxToRem(11),
    lineHeight: 1.2,
    letterSpacing: "0.02em",
    textTransform: "capitalize" as const,
  },
  statusLabelMd: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 600,
    fontSize: pxToRem(12),
    lineHeight: 1.2,
    letterSpacing: "0.02em",
    textTransform: "capitalize" as const,
  },
  cellPrimary: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 400,
    fontSize: pxToRem(13),
    lineHeight: 1.4,
  },
  cellSecondary: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 400,
    fontSize: pxToRem(11),
    lineHeight: 1.4,
  },
  amount: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 500,
    fontSize: pxToRem(13),
    lineHeight: 1.4,
    ...tabular,
  },
  amountStrong: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 600,
    fontSize: pxToRem(14),
    lineHeight: 1.15,
    ...tabular,
  },
  cellMono: {
    fontFamily: FONT_MONO,
    fontWeight: 400,
    fontSize: pxToRem(12),
    lineHeight: 1.4,
  },
  cellMonoSm: {
    fontFamily: FONT_MONO,
    fontWeight: 400,
    fontSize: pxToRem(11),
    lineHeight: 1.4,
    ...tabular,
  },
  actionLink: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 600,
    fontSize: pxToRem(12),
    lineHeight: 1.5,
  },
} as const;

declare module "@mui/material/styles" {
  interface TypographyVariants {
    pageTitle: React.CSSProperties;
    pageMeta: React.CSSProperties;
    sectionTitle: React.CSSProperties;
    sectionLabel: React.CSSProperties;
    heroAmount: React.CSSProperties;
    heroAmountSm: React.CSSProperties;
    ticker: React.CSSProperties;
    tickerSm: React.CSSProperties;
    networkBadge: React.CSSProperties;
    chipLabel: React.CSSProperties;
    statusLabel: React.CSSProperties;
    statusLabelMd: React.CSSProperties;
    cellPrimary: React.CSSProperties;
    cellSecondary: React.CSSProperties;
    amount: React.CSSProperties;
    amountStrong: React.CSSProperties;
    cellMono: React.CSSProperties;
    cellMonoSm: React.CSSProperties;
    actionLink: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    pageTitle?: React.CSSProperties;
    pageMeta?: React.CSSProperties;
    sectionTitle?: React.CSSProperties;
    sectionLabel?: React.CSSProperties;
    heroAmount?: React.CSSProperties;
    heroAmountSm?: React.CSSProperties;
    ticker?: React.CSSProperties;
    tickerSm?: React.CSSProperties;
    networkBadge?: React.CSSProperties;
    chipLabel?: React.CSSProperties;
    statusLabel?: React.CSSProperties;
    statusLabelMd?: React.CSSProperties;
    cellPrimary?: React.CSSProperties;
    cellSecondary?: React.CSSProperties;
    amount?: React.CSSProperties;
    amountStrong?: React.CSSProperties;
    cellMono?: React.CSSProperties;
    cellMonoSm?: React.CSSProperties;
    actionLink?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    pageTitle: true;
    pageMeta: true;
    sectionTitle: true;
    sectionLabel: true;
    heroAmount: true;
    heroAmountSm: true;
    ticker: true;
    tickerSm: true;
    networkBadge: true;
    chipLabel: true;
    statusLabel: true;
    statusLabelMd: true;
    cellPrimary: true;
    cellSecondary: true;
    amount: true;
    amountStrong: true;
    cellMono: true;
    cellMonoSm: true;
    actionLink: true;
  }
}

export default typography as TypographyOptions;
